import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '@shared/services/auth.service';
import { TenantService } from '@core/tenant/tenant.service';
import { environment } from '../../../environments/environment';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const tenantService = inject(TenantService);
  const router = inject(Router);

  if (!req.url.startsWith(environment.baseUrl)) {
    return next(req);
  }

  const accessToken = authService.getToken();
  let authReq = injectHeaders(req, accessToken, tenantService.getCurrentTenantId());

  return next(authReq).pipe(
    catchError((error) => {
      const shouldRefresh = error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !req.url.includes('/refresh-token');

      if (shouldRefresh) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return authService.refreshToken().pipe(
            switchMap((response) => {
              isRefreshing = false;
              refreshTokenSubject.next(response.access_token);

              return next(injectHeaders(req, response.access_token, tenantService.getCurrentTenantId()));
            }),
            catchError((refreshError) => {
              isRefreshing = false;
              authService.clearSession();
              router.navigate(['/login']);
              return throwError(() => refreshError);
            })
          );
        } else {
          return refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(newToken => next(injectHeaders(req, newToken!, tenantService.getCurrentTenantId())))
          );
        }
      }

      return throwError(() => error);
    })
  );
};

function injectHeaders(req: HttpRequest<unknown>, token: string | null, tenantId: string) {
  let headers = req.headers.set('X-Tenant-Id', tenantId);

  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  return req.clone({ headers });
}